import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import './UhtCashUsed.css';
import './Theme.css';
import { getUrlParams } from '../apiService/timespam';
import { lifestyleApi } from '../apiService/api';

// Tab definitions: id maps to paginationTab name + response data key
const TABS = [
  { id: 'all',      paginationName: 'alltabspagination',     dataKey: 'ledger',        labelKey: 'uhtCash.tabs.all' },
  { id: 'rewarded', paginationName: 'earnedtabpagination',   dataKey: 'ledgerEarned',  labelKey: 'uhtCash.tabs.rewarded' },
  { id: 'redeemed', paginationName: 'burnedtabpagination',   dataKey: 'ledgerBurned',  labelKey: 'uhtCash.tabs.redeemed' },
  { id: 'pending',  paginationName: 'pendingtabpagination',  dataKey: 'pendingtab',    labelKey: 'uhtCash.tabs.pending' },
];

// Map API colorCodeClass → CSS class
// Known values: "text-rewarded", "text-redeemed", "text-burned", "text-pending"
const pointClass = (colorClass) => {
  if (!colorClass) return 'uht-pts-default';
  const c = colorClass.toLowerCase();
  if (c.includes('reward') || c.includes('earn') || c.includes('credit')) return 'uht-pts-green';
  if (c.includes('redeem') || c.includes('burn') || c.includes('debit'))  return 'uht-pts-red';
  return 'uht-pts-default';
};

const UhtCashUsed = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();

  // Read URL params
  const searchParams = new URLSearchParams(window.location.search);
  // 'list' is the encrypted userId used by the PHP backend; fallback to goqiiuserid
  const listParam = searchParams.getAll('list')[0] || '';
  const goqiiUserId = searchParams.get('goqiiuserid') || searchParams.get('goqiiUserId') || '';

  // State
  const [activeTab, setActiveTab]       = useState(0); // index into TABS
  const [balance, setBalance]           = useState(null);
  const [rows, setRows]                 = useState([]);
  const [pagination, setPagination]     = useState(0);
  const [loading, setLoading]           = useState(true);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [hasMore, setHasMore]           = useState(true);
  const [error, setError]               = useState(false);
  const abortRef = useRef(null);

  // ── Theme + Lang setup ──────────────────────────────────────────────
  useEffect(() => {
    document.documentElement.className = theme === 'dark' ? 'dark' : '';
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    if (lang && i18n.language !== lang) i18n.changeLanguage(lang);
  }, [theme, lang]);

  // ── Fetch data ───────────────────────────────────────────────────────
  const fetchRows = useCallback(async (tabIndex, page, append = false) => {
    const tab = TABS[tabIndex];
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController(); // kept for ref pattern, axios handles cancellation internally

    try {
      append ? setLoadingMore(true) : setLoading(true);
      setError(false);

      const res = await lifestyleApi.fetchUhtCashDetails({
        userId:        listParam || goqiiUserId,
        paginationTab: tab.paginationName,
        pagination:    page,
      });

      const data = res?.data?.data || {};

      // Balance only on first page load
      if (!append && page === 0) {
        setBalance(data.totalUHTPoints ?? null);
      }

      const items = data[tab.dataKey] || [];
      const nextPage = data[tab.paginationName] ?? null;

      setRows(prev => append ? [...prev, ...items] : items);
      setPagination(nextPage ?? page);
      setHasMore(Array.isArray(items) && items.length > 0 && nextPage !== null && nextPage !== page);
    } catch (err) {
      if (err.name !== 'CanceledError') setError(true);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [listParam, goqiiUserId]);

  // Reset + fetch when tab changes
  useEffect(() => {
    setRows([]);
    setPagination(0);
    setHasMore(true);
    fetchRows(activeTab, 0, false);
  }, [activeTab, fetchRows]);

  // ── Infinite scroll ──────────────────────────────────────────────────
  const scrollRef = useRef(null);
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      fetchRows(activeTab, pagination, true);
    }
  }, [activeTab, pagination, loadingMore, hasMore, fetchRows]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // ── Tab column header label ──────────────────────────────────────────
  const thirdColLabel = () => {
    switch (activeTab) {
      case 1: return t('uhtCash.cols.rewarded');
      case 2: return t('uhtCash.cols.redeemed');
      case 3: return t('uhtCash.cols.pending');
      default: return t('uhtCash.cols.all');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="uht-page" ref={scrollRef}>

      {/* Banner */}
      <div className="uht-banner">
        <p className="uht-banner-title">{t('uhtCash.balanceTitle')}</p>
        <div className="uht-balance-pill">
          <img
            src="https://appcdn.goqii.com/storeimg/45490_1699427417.png"
            className="uht-balance-icon"
            alt="UHT"
          />
          <span className="uht-balance-value">
            {balance !== null ? balance : '—'}
          </span>
        </div>
      </div>

      {/* Tab bar */}
      <div className="uht-tabs">
        {TABS.map((tab, i) => (
          <button
            key={tab.id}
            className={`uht-tab${activeTab === i ? ' active' : ''}`}
            onClick={() => setActiveTab(i)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="uht-center-state">
          <img
            src={
              theme === 'dark'
                ? 'https://appcdn.goqii.com/storeimg/44438_1778481784.png'
                : 'https://appcdn.goqii.com/storeimg/11129_1725520285.jpg'
            }
            className="uht-loading-logo"
            alt="loading"
          />
          <span>{t('uhtCash.loading')}</span>
        </div>
      ) : error ? (
        <div className="uht-center-state">
          <span>{t('uhtCash.error')}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="uht-center-state">
          <span>{t('uhtCash.noData')}</span>
        </div>
      ) : (
        <>
          {/* Table header */}
          <div className="uht-table-header">
            <span className="uht-th-date">{t('uhtCash.cols.date')}</span>
            <span className="uht-th-desc">{t('uhtCash.cols.description')}</span>
            <span className="uht-th-pts">{thirdColLabel()}</span>
          </div>

          {/* Rows */}
          <div className="uht-list">
            {rows.map((item, idx) => (
              <div className="uht-row" key={idx}>
                <div className="uht-date-badge">{item.date}</div>
                <div className="uht-desc-col">
                  <div className="uht-desc-text">{item.eventTitle}</div>
                  {item.expiresOn ? (
                    <div className="uht-expires-text">{item.expiresOn}</div>
                  ) : null}
                </div>
                <div className={`uht-pts-col ${pointClass(item.colorCodeClass)}`}>
                  {item.rewardPoints}
                </div>
              </div>
            ))}

            {/* Load-more spinner */}
            {loadingMore && (
              <div className="uht-load-more">
                <div className="uht-spinner" />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UhtCashUsed;
